import React from 'react';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { pathByRouteName } from '../../../util/routes';
import { requiredFieldArrayCheckbox } from '../../../util/validators';
import { FieldCheckboxGroup } from '../../../components';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';

import css from './TermsAndConditions.module.css';

const KEY_CODE_ENTER = 13;

/**
 * A component that renders the terms and conditions.
 *
 * Both documents open in a modal on the auth page. Inside the Topbar login dialog there is no
 * second modal layer to open them into — stacking one dialog on another traps focus and, on iOS,
 * leaves the page scroll-locked after the inner one closes. So when no open-handler is passed,
 * each reference renders as an ordinary link to its own page in a new tab: the half-filled signup
 * form stays exactly as it was.
 *
 * @component
 * @param {Object} props
 * @param {Function} [props.onOpenTermsOfService] - Opens the terms modal; omit for plain links
 * @param {Function} [props.onOpenPrivacyPolicy] - Opens the privacy modal; omit for plain links
 * @param {string} props.formId - The form id
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const TermsAndConditions = props => {
  const { onOpenTermsOfService, onOpenPrivacyPolicy, formId, intl } = props;
  const routeConfiguration = useRouteConfiguration();

  const handleClick = callback => e => {
    e.preventDefault();
    callback(e);
  };
  const handleKeyUp = callback => e => {
    // Allow click action with keyboard like with normal links
    if (e.keyCode === KEY_CODE_ENTER) {
      callback();
    }
  };

  // `target="_blank"` and not a same-tab link: leaving the page to read the terms would discard
  // everything typed into the signup form so far.
  const documentReference = (onOpen, routeName, className, messageId) =>
    onOpen ? (
      <span
        className={className}
        onClick={handleClick(onOpen)}
        role="button"
        tabIndex="0"
        onKeyUp={handleKeyUp(onOpen)}
      >
        <FormattedMessage id={messageId} />
      </span>
    ) : (
      // A plain anchor, not NamedLink: NamedLink renders a React Router Link and forwards only a
      // fixed set of props, so `target` would be dropped and the click would navigate this tab.
      <a
        href={pathByRouteName(routeName, routeConfiguration)}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        <FormattedMessage id={messageId} />
      </a>
    );

  const termsLink = documentReference(
    onOpenTermsOfService,
    'TermsOfServicePage',
    css.termsLink,
    'AuthenticationPage.termsAndConditionsTermsLinkText'
  );

  const privacyLink = documentReference(
    onOpenPrivacyPolicy,
    'PrivacyPolicyPage',
    css.privacyLink,
    'AuthenticationPage.termsAndConditionsPrivacyLinkText'
  );

  return (
    <div className={css.root}>
      <FieldCheckboxGroup
        name="terms"
        id={formId ? `${formId}.terms-accepted` : 'terms-accepted'}
        optionLabelClassName={css.finePrint}
        options={[
          {
            key: 'tos-and-privacy',
            label: intl.formatMessage(
              { id: 'AuthenticationPage.termsAndConditionsAcceptText' },
              { termsLink, privacyLink }
            ),
          },
        ]}
        validate={requiredFieldArrayCheckbox(
          intl.formatMessage({ id: 'AuthenticationPage.termsAndConditionsAcceptRequired' })
        )}
      />
    </div>
  );
};

export default TermsAndConditions;
